/**
 * The text-contrast lint rule. Async: it rasterizes each frame and samples the real rendered pixels
 * behind every text element, so contrast is correct even over images, gradients, and video where CSS
 * colours alone can't tell you the background. Reports each element once, at its worst frame.
 */
import {
    contrastRatio,
    isLargeText,
    meetsAA,
    parseColor,
    sampleBackgroundUnderGlyph,
    seeThroughShareUnderGlyph,
    sitsOnSceneBeneath,
    type ImageLike,
} from './contrast';
import { describeElement, intersectRects, ownElementText, visibleThroughWindows } from './measure';
import type { ElementMeasurement, Rect } from './measure';
import type { RuntimeLintContext, RuntimeLintRule } from './runtimeRules';

// Contrast is only assessed where the text is (near-)fully rendered. Below this, a fading glyph is
// rasterized as a blend of foreground and background; those blended pixels sit far enough from the
// full-strength CSS `color` that the sampler would misread them as background and under-report the
// ratio — a false positive on text that merely fades in/out. A translucent `color` alpha causes the
// same blend, so it's gated too.
const OPAQUE_THRESHOLD = 0.95;

/**
 * Whether a measured text element is rendered solidly enough at this frame to assess its contrast:
 * it has measurable text, is near-fully opaque, and its colour is not translucent. Fade frames and
 * translucent colours are skipped rather than risk a blended-pixel false positive.
 */
export function shouldAssessContrast(measurement: ElementMeasurement): boolean {
    if (!measurement.hasText || measurement.fontSizePx <= 0) return false;
    if (measurement.opacity < OPAQUE_THRESHOLD) return false;
    const foreground = parseColor(measurement.color);
    if (!foreground) return false;
    return (foreground.a ?? 1) >= OPAQUE_THRESHOLD;
}

function isEmpty(rect: Rect): boolean {
    return rect.width <= 0 || rect.height <= 0;
}

function withoutCover(rect: Rect, cover: Rect): Rect {
    const overlap = intersectRects(rect, cover);
    if (isEmpty(overlap)) return rect;
    const right = rect.x + rect.width;
    const bottom = rect.y + rect.height;
    const coverRight = cover.x + cover.width;
    const coverBottom = cover.y + cover.height;
    const spansHeight = cover.y <= rect.y && coverBottom >= bottom;
    const spansWidth = cover.x <= rect.x && coverRight >= right;
    if (spansHeight && spansWidth) return { ...rect, width: 0, height: 0 };
    if (spansHeight && cover.x <= rect.x)
        return { ...rect, x: coverRight, width: right - coverRight };
    if (spansHeight && coverRight >= right) return { ...rect, width: cover.x - rect.x };
    if (spansWidth && cover.y <= rect.y)
        return { ...rect, y: coverBottom, height: bottom - coverBottom };
    if (spansWidth && coverBottom >= bottom) return { ...rect, height: cover.y - rect.y };
    return rect;
}

function childOf(ancestor: Element, element: Element): Element | null {
    let node: Element | null = element;
    while (node && node.parentElement !== ancestor) node = node.parentElement;
    return node;
}

function isOpaqueLayer(measurement: ElementMeasurement): boolean {
    const position = measurement.computed?.position;
    if (!position || position === 'static') return false;
    if (measurement.opacity < OPAQUE_THRESHOLD) return false;
    const background = parseColor(measurement.computed?.backgroundColor ?? '');
    return !!background && (background.a ?? 1) >= OPAQUE_THRESHOLD;
}

function zIndexOf(measurement: ElementMeasurement | undefined): number {
    return parseInt(measurement?.computed?.zIndex ?? '', 10) || 0;
}

/** The layer holding `text` that `layer` is painted over, or null when it is painted beneath. */
function layerPaintedOver(
    layer: ElementMeasurement,
    text: ElementMeasurement,
    byElement: Map<Element, ElementMeasurement>,
): Element | null {
    const parent = layer.element.parentElement;
    if (!parent || !parent.contains(text.element)) return null;
    const textSide = childOf(parent, text.element);
    if (!textSide || textSide === layer.element) return null;
    const following =
        textSide.compareDocumentPosition(layer.element) & Node.DOCUMENT_POSITION_FOLLOWING;
    if (!following) return null;
    const textLayer = byElement.get(textSide);
    const textPosition = textLayer?.computed?.position;
    if (!textPosition || textPosition === 'static') return textSide;
    return zIndexOf(textLayer) <= zIndexOf(layer) ? textSide : null;
}

function pathFrom(ancestor: Element, element: Element): number[] {
    const path: number[] = [];
    for (let node = element; node !== ancestor && node.parentElement; node = node.parentElement) {
        path.unshift(Array.prototype.indexOf.call(node.parentElement.children, node));
    }
    return path;
}

function redrawsCopy(layer: Element, textSide: Element, text: Element): boolean {
    let counterpart: Element | undefined = layer;
    for (const index of pathFrom(textSide, text)) {
        counterpart = counterpart.children[index];
        if (!counterpart) return false;
    }
    return counterpart !== layer && ownElementText(counterpart) === ownElementText(text);
}

/**
 * The part of `rect` a viewer can see of this copy: what its clip-paths leave, less whatever an
 * opaque layer painted over it covers when that layer duplicates the one the copy sits in and draws
 * the same letters again in its own colours.
 */
function shownPart(
    rect: Rect,
    text: ElementMeasurement,
    layers: ElementMeasurement[],
    byElement: Map<Element, ElementMeasurement>,
    canvasSize: RuntimeLintContext['canvasSize'],
): Rect {
    const onCanvas = canvasSize
        ? intersectRects(rect, { x: 0, y: 0, width: canvasSize.width, height: canvasSize.height })
        : rect;
    let shown = text.clipPathRect ? intersectRects(onCanvas, text.clipPathRect) : onCanvas;
    for (const layer of layers) {
        if (isEmpty(shown)) break;
        const textSide = layerPaintedOver(layer, text, byElement);
        if (!textSide || !redrawsCopy(layer.element, textSide, text.element)) continue;
        const painted = visibleThroughWindows(
            layer.clipPathRect ? intersectRects(layer.rect, layer.clipPathRect) : layer.rect,
            layer.clipRect,
            canvasSize,
        );
        shown = withoutCover(shown, painted);
    }
    return shown;
}

interface Offence {
    element: Element;
    ratio: number;
    large: boolean;
}

export const textContrastBelowAa: RuntimeLintRule = {
    id: 'text-contrast-below-aa',
    severity: 'error',
    guidance:
        'Keep text contrast at WCAG AA against whatever it actually sits on (4.5:1 for body text, ' +
        '3:1 for large text). Over a busy image or gradient, add a scrim/overlay or a solid text ' +
        'background so the copy stays legible.',
    async check(context: RuntimeLintContext): Promise<string[]> {
        const frames = context.frames ?? [];
        if (frames.length === 0) return [];

        // Worst (lowest) failing ratio per element across frames.
        const worst = new Map<Element, Offence>();
        for (const frame of frames) {
            const texts = frame.elements.filter(shouldAssessContrast);
            if (texts.length === 0) continue;

            let image: ImageLike | null = null;
            try {
                const canvas = await frame.rasterize();
                const context2d = canvas.getContext('2d');
                if (!context2d) continue;
                image = context2d.getImageData(0, 0, canvas.width, canvas.height);
            } catch {
                continue; // a frame that won't rasterize can't be assessed; skip it
            }

            const byElement = new Map(frame.elements.map((m) => [m.element, m]));
            const layers = frame.elements.filter(isOpaqueLayer);
            for (const measurement of texts) {
                const foreground = parseColor(measurement.color);
                if (!foreground) continue;
                // Copy waiting behind a closed mask is not on screen, whatever its box sits over.
                // Judged on the letterforms, since a large capital's ascent reaches into the window
                // well before any of its ink does.
                const glyphsShowing = shownPart(
                    visibleThroughWindows(
                        measurement.glyphRect ?? measurement.inkRect,
                        measurement.clipRect,
                        context.canvasSize,
                    ),
                    measurement,
                    layers,
                    byElement,
                    context.canvasSize,
                );
                if (isEmpty(glyphsShowing)) continue;
                const sampled = shownPart(
                    measurement.inkRect,
                    measurement,
                    layers,
                    byElement,
                    context.canvasSize,
                );
                if (isEmpty(sampled)) continue;
                // Over a scene, a see-through backdrop is content this raster does not contain —
                // text-without-plate reports that, rather than a ratio against nothing.
                if (
                    context.overlaysScene &&
                    sitsOnSceneBeneath(
                        seeThroughShareUnderGlyph(
                            image,
                            visibleThroughWindows(
                                measurement.inkRect,
                                measurement.clipRect,
                                context.canvasSize,
                            ),
                            foreground,
                        ),
                    )
                ) {
                    continue;
                }
                const background = sampleBackgroundUnderGlyph(image, sampled, foreground);
                if (!background) continue;
                const ratio = contrastRatio(foreground, background);
                const large = isLargeText(measurement.fontSizePx, measurement.bold);
                if (meetsAA(ratio, large)) continue;
                const previous = worst.get(measurement.element);
                if (!previous || ratio < previous.ratio) {
                    worst.set(measurement.element, { element: measurement.element, ratio, large });
                }
            }
        }

        return [...worst.values()].map(
            ({ element, ratio, large }) =>
                `${describeElement(element)} renders at ${ratio.toFixed(2)}:1 contrast against its ` +
                `background — below WCAG AA (${large ? '3:1 for large text' : '4.5:1 for body text'}). ` +
                `Adjust the colour, or add a scrim behind the text — one that reads as a panel, ` +
                `not a patch of the page colour, which would hide whatever it covers.`,
        );
    },
};
